import { useCallback, useEffect, useRef, useState } from "react";
import { T, base } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { cleanPhone, timeAgo } from "../lib/utils";
import { Btn } from "../components";

// ── Constants ──────────────────────────────────────────────────────
const LEAD_STATUSES = [
  { value: "",          label: "No Status",  color: "#9ca3af" },
  { value: "cold",      label: "Cold",       color: "#64748b" },
  { value: "warm",      label: "Warm",       color: "#f59e0b" },
  { value: "hot",       label: "Hot",        color: "#f43f5e" },
  { value: "converted", label: "Converted",  color: "#6366f1" },
  { value: "lost",      label: "Lost",       color: "#9ca3af" },
];
const PRESET_TAGS   = ["VIP", "Follow up", "New", "Returning", "High value", "Needs nurturing", "Not interested"];
const statusColor   = (s) => LEAD_STATUSES.find(l => l.value === s)?.color || "#9ca3af";
const statusLabel   = (s) => LEAD_STATUSES.find(l => l.value === s)?.label || "";

// ── Avatar ─────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  ["rgba(99,102,241,0.22)","#6366f1"],
  ["rgba(139,92,246,0.22)","#8b5cf6"],
  ["rgba(56,189,248,0.18)","#38bdf8"],
  ["rgba(20,184,166,0.18)","#14b8a6"],
  ["rgba(245,158,11,0.18)","#f59e0b"],
  ["rgba(244,63,94,0.18)", "#f43f5e"],
];
const Avatar = ({ name, size = 40 }) => {
  const idx = (name || "?").charCodeAt(0) % AVATAR_COLORS.length;
  const [bg, fg] = AVATAR_COLORS[idx];
  return (
    <div style={{ width: size, height: size, borderRadius: size * 0.30, background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.40, fontWeight: 700, color: fg, flexShrink: 0, letterSpacing: "-0.02em" }}>
      {(name || "?")[0].toUpperCase()}
    </div>
  );
};

const Tag = ({ label, onRemove }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 20, background: "rgba(139,92,246,0.12)", color: T.violet, fontSize: 11, fontWeight: 600, border: "1px solid rgba(139,92,246,0.2)" }}>
    {label}
    {onRemove && <span onClick={onRemove} style={{ cursor: "pointer", opacity: 0.6, fontSize: 13, lineHeight: 1, marginTop: -1 }}>×</span>}
  </span>
);

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 10, fontWeight: 700, color: T.text3, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5 }}>{label}</div>
    {children}
  </div>
);

const PanelInput = ({ value, onChange, placeholder, style = {} }) => (
  <input value={value || ""} onChange={onChange} placeholder={placeholder}
    style={{ width: "100%", padding: "8px 11px", background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 8, color: T.text1, fontSize: 12, outline: "none", boxSizing: "border-box", fontFamily: base.font, ...style }}
    onFocus={e => (e.target.style.borderColor = T.indigo)}
    onBlur={e  => (e.target.style.borderColor = T.border1)} />
);
const PanelTextarea = ({ value, onChange, placeholder, rows = 3 }) => (
  <textarea value={value || ""} onChange={onChange} placeholder={placeholder} rows={rows}
    style={{ width: "100%", padding: "8px 11px", background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 8, color: T.text1, fontSize: 12, outline: "none", boxSizing: "border-box", resize: "vertical", fontFamily: base.font, lineHeight: 1.5 }}
    onFocus={e => (e.target.style.borderColor = T.indigo)}
    onBlur={e  => (e.target.style.borderColor = T.border1)} />
);

// ── Contact card ───────────────────────────────────────────────────
const ContactCard = ({ c, isActive, onClick, selectMode, isSelected, onToggleSelect }) => {
  const sc          = statusColor(c.leadStatus);
  const sl          = statusLabel(c.leadStatus);
  const displayName = c.name || c.phone || cleanPhone(c.contact);

  const handleClick = (e) => {
    if (selectMode) { e.stopPropagation(); onToggleSelect(); }
    else onClick();
  };

  return (
    <div
      onClick={handleClick}
      className="card-hover"
      style={{
        background: isSelected ? "rgba(99,102,241,0.10)" : T.bg2,
        border: `1px solid ${isSelected ? "#6366f1" : isActive ? T.indigo + "55" : T.border1}`,
        borderRadius: 14, padding: "16px", cursor: "pointer",
        transition: "all 0.18s", position: "relative",
        boxShadow: isActive && !selectMode ? `0 0 0 2px ${T.indigo}33` : "none",
      }}
    >
      {/* Select checkbox */}
      {selectMode && (
        <div style={{
          position: "absolute", top: 10, right: 10,
          width: 18, height: 18, borderRadius: 5,
          background: isSelected ? "#6366f1" : T.bg0,
          border: `2px solid ${isSelected ? "#6366f1" : T.border2}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          zIndex: 2, transition: "all 0.15s",
        }}>
          {isSelected && (
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      )}

      {/* Active indicator (non-select mode) */}
      {isActive && !selectMode && (
        <div style={{ position: "absolute", top: 12, right: 12, width: 8, height: 8, borderRadius: "50%", background: T.indigo }} />
      )}

      {/* Top row */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 12 }}>
        <Avatar name={displayName} size={42} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginBottom: 2 }}>
            {displayName}
          </div>
          <div style={{ fontSize: 11, color: T.text3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {c.phone || cleanPhone(c.contact) || "No phone"}
          </div>
        </div>
      </div>

      {c.industry && (
        <div style={{ fontSize: 11, color: T.sky, marginBottom: 8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {c.industry}
        </div>
      )}

      {c.tags?.length > 0 && (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 10 }}>
          {c.tags.slice(0, 2).map(t => (
            <span key={t} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 10, background: "rgba(139,92,246,0.10)", color: T.violet, border: "1px solid rgba(139,92,246,0.18)" }}>{t}</span>
          ))}
          {c.tags.length > 2 && <span style={{ fontSize: 10, color: T.text3 }}>+{c.tags.length - 2}</span>}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 10, borderTop: `1px solid ${T.border1}` }}>
        {sl ? (
          <span style={{ fontSize: 10, fontWeight: 700, color: sc, background: `${sc}18`, padding: "2px 8px", borderRadius: 10, border: `1px solid ${sc}30` }}>{sl}</span>
        ) : <span />}
        <span style={{ fontSize: 10, color: T.text3 }}>{c.lastContactedAt ? timeAgo(c.lastContactedAt) : "Never"}</span>
      </div>
    </div>
  );
};

// ── Main ───────────────────────────────────────────────────────────
export default function ContactsPage({ token, showToast }) {
  const [contacts,    setContacts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selected,    setSelected]    = useState(null);
  const [profile,     setProfile]     = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [deleting,    setDeleting]    = useState(false);
  const [confirmDel,  setConfirmDel]  = useState(false);
  const [search,      setSearch]      = useState("");
  const [syncing,     setSyncing]     = useState(false);
  const [activeTab,   setActiveTab]   = useState("profile");
  const [tagInput,    setTagInput]    = useState("");
  // multi-select
  const [selectMode,  setSelectMode]  = useState(false);
  const [selection,   setSelection]   = useState(new Set());
  const [bulkDeleting,setBulkDeleting]= useState(false);
  const chatEndRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ ...(search && { search }) });
      const d = await apiFetch(`/api/contacts?${params}`, {}, token);
      setContacts(d.contacts || []);
    } catch { showToast?.("Failed to load contacts", "error"); }
    setLoading(false);
  }, [token, search]);

  const sync = useCallback(async (silent = false) => {
    if (!silent) setSyncing(true);
    try { await apiFetch("/api/contacts/import", { method: "POST" }, token); }
    catch { /* silent */ }
    if (!silent) setSyncing(false);
  }, [token]);

  useEffect(() => { sync(true).then(() => load()); }, []);
  useEffect(() => { load(); }, [load]);

  const openContact = (c) => {
    if (selectMode) return;
    setSelected(c.contact);
    setActiveTab("profile");
    setChatHistory([]);
    setConfirmDel(false);
    setProfile({
      name:            c.name || c.contactName || "",
      phone:           c.phone || cleanPhone(c.contact),
      industry:        c.industry || "",
      interestedIn:    c.interestedIn || "",
      intention:       c.intention || "",
      leadStatus:      c.leadStatus || "",
      tags:            c.tags || [],
      notes:           c.notes || "",
      lastContactedAt: c.lastContactedAt || null,
      autoImported:    c.autoImported || false,
    });
  };

  const loadChat = async (contact) => {
    setChatLoading(true);
    try {
      const d = await apiFetch(`/api/chats/${encodeURIComponent(contact)}`, {}, token);
      setChatHistory(d.messages || []);
    } catch { setChatHistory([]); }
    setChatLoading(false);
  };
  useEffect(() => {
    if (activeTab === "chat" && selected) loadChat(selected);
  }, [activeTab, selected]);
  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [chatHistory]);

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await apiFetch(`/api/contacts/${encodeURIComponent(selected)}`, {
        method: "PATCH", body: JSON.stringify(profile),
      }, token);
      showToast?.("Contact saved");
      await load();
    } catch { showToast?.("Save failed", "error"); }
    setSaving(false);
  };

  const refresh = async () => {
    setSyncing(true);
    try {
      const d = await apiFetch("/api/contacts/import", { method: "POST" }, token);
      showToast?.(`Synced ${d.imported ?? ""} contacts`);
      await load();
    } catch { showToast?.("Sync failed", "error"); }
    setSyncing(false);
  };

  // ── Single delete (contact + chat) ───────────────────────────────
  const deleteProfile = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/contacts/${encodeURIComponent(selected)}`, { method: "DELETE" }, token);
      // Also wipe chat history so it doesn't linger in ChatsPage
      await apiFetch(`/api/chats/${encodeURIComponent(selected)}`, { method: "DELETE" }, token).catch(() => {});
      showToast?.("Contact & chat deleted");
      setSelected(null); setProfile(null); setConfirmDel(false);
      await load();
    } catch { showToast?.("Delete failed", "error"); }
    setDeleting(false);
  };

  // ── Bulk delete ───────────────────────────────────────────────────
  const bulkDelete = async () => {
    if (!selection.size) return;
    setBulkDeleting(true);
    let ok = 0;
    for (const contact of selection) {
      try {
        await apiFetch(`/api/contacts/${encodeURIComponent(contact)}`, { method: "DELETE" }, token);
        await apiFetch(`/api/chats/${encodeURIComponent(contact)}`, { method: "DELETE" }, token).catch(() => {});
        ok++;
      } catch { /* continue */ }
    }
    showToast?.(`Deleted ${ok} contact${ok !== 1 ? "s" : ""} & their chats`);
    setSelection(new Set());
    setSelectMode(false);
    if (selection.has(selected)) { setSelected(null); setProfile(null); }
    await load();
    setBulkDeleting(false);
  };

  const toggleSelect = (contact) => {
    setSelection(prev => {
      const next = new Set(prev);
      next.has(contact) ? next.delete(contact) : next.add(contact);
      return next;
    });
  };
  const selectAll  = () => setSelection(new Set(displayed.map(c => c.contact)));
  const clearSel   = () => setSelection(new Set());
  const exitSelect = () => { setSelectMode(false); clearSel(); };

  const addTag    = (tag) => { if (!tag || profile.tags?.includes(tag)) return; setProfile(p => ({ ...p, tags: [...(p.tags || []), tag] })); setTagInput(""); };
  const removeTag = (tag) => setProfile(p => ({ ...p, tags: (p.tags || []).filter(t => t !== tag) }));

  const displayed = contacts.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || "").toLowerCase().includes(q) ||
           (c.phone || "").includes(q) ||
           (c.industry || "").toLowerCase().includes(q);
  });

  return (
    <div style={{ flex: 1, display: "flex", overflow: "hidden", position: "relative" }}>

      {/* ── MAIN: card grid ─────────────────────────────────────── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* Top bar */}
        <div style={{
          padding: "12px 20px", borderBottom: `1px solid ${T.border1}`,
          background: T.bg1, display: "flex", alignItems: "center", gap: 10, flexShrink: 0,
        }}>
          {selectMode ? (
            /* ── Bulk-select toolbar ── */
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.text1 }}>
                {selection.size} selected
              </div>
              <button onClick={selectAll} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${T.border1}`, background: T.bg2, color: T.text2, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: base.font }}>
                Select all ({displayed.length})
              </button>
              <button onClick={clearSel} style={{ padding: "5px 12px", borderRadius: 8, border: `1px solid ${T.border1}`, background: T.bg2, color: T.text2, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: base.font }}>
                Clear
              </button>
              <div style={{ flex: 1 }} />
              {selection.size > 0 && (
                <button onClick={bulkDelete} disabled={bulkDeleting}
                  style={{ padding: "6px 16px", borderRadius: 9, border: "none", background: "#f43f5e", color: "#fff", fontSize: 12, fontWeight: 700, cursor: bulkDeleting ? "not-allowed" : "pointer", fontFamily: base.font }}>
                  {bulkDeleting ? "Deleting…" : `Delete ${selection.size}`}
                </button>
              )}
              <button onClick={exitSelect}
                style={{ padding: "6px 14px", borderRadius: 9, border: `1px solid ${T.border1}`, background: T.bg2, color: T.text2, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: base.font }}>
                Cancel
              </button>
            </>
          ) : (
            /* ── Normal toolbar ── */
            <>
              <div style={{ fontSize: 15, fontWeight: 700, color: T.text1, whiteSpace: "nowrap" }}>
                Contacts
                <span style={{ fontSize: 12, color: T.text3, fontWeight: 400, marginLeft: 8 }}>({contacts.length})</span>
              </div>

              <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, industry…"
                  style={{ width: "100%", padding: "7px 12px 7px 32px", background: T.bg2, border: `1px solid ${T.border1}`, borderRadius: 9, color: T.text1, fontSize: 12, outline: "none", boxSizing: "border-box" }}
                  onFocus={e => (e.target.style.borderColor = T.indigo)}
                  onBlur={e  => (e.target.style.borderColor = T.border1)} />
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2" style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              </div>

              <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                <button
                  onClick={() => { setSelectMode(true); clearSel(); }}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 13px", borderRadius: 9, border: `1px solid ${T.border1}`, background: T.bg2, color: T.text2, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: base.font }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="5" height="5" rx="1"/><rect x="10" y="3" width="5" height="5" rx="1"/><rect x="3" y="10" width="5" height="5" rx="1"/><polyline points="10,12 15,17 22,10"/></svg>
                  Select
                </button>
                <button onClick={refresh} disabled={syncing}
                  style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 13px", borderRadius: 9, border: `1px solid ${T.border1}`, background: T.bg2, color: T.text2, fontSize: 12, fontWeight: 600, cursor: syncing ? "not-allowed" : "pointer", fontFamily: base.font }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: syncing ? "spin 0.8s linear infinite" : "none" }}><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.5"/></svg>
                  {syncing ? "Syncing…" : "Sync"}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflowY: "auto", padding: "18px 20px" }}>
          {loading ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))", gap: 13 }}>
              {[...Array(8)].map((_, i) => <div key={i} className="skeleton" style={{ height: 130, borderRadius: 14 }} />)}
            </div>
          ) : displayed.length === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0", gap: 12 }}>
              <div style={{ opacity: 0.2 }}>
                <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: T.text2 }}>{search ? "No contacts found" : "No contacts yet"}</div>
              {!search && <div style={{ fontSize: 13, color: T.text3, textAlign: "center", maxWidth: 320, lineHeight: 1.6 }}>Contacts are synced automatically from your WhatsApp chats</div>}
              {!search && (
                <button onClick={refresh} disabled={syncing}
                  style={{ padding: "9px 20px", borderRadius: 10, border: "none", cursor: "pointer", fontFamily: base.font, fontSize: 13, fontWeight: 700, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff" }}>
                  {syncing ? "Syncing…" : "Sync from WhatsApp"}
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(210px,1fr))", gap: 13 }}>
              {displayed.map(c => (
                <ContactCard
                  key={c.contact}
                  c={c}
                  isActive={selected === c.contact}
                  onClick={() => openContact(c)}
                  selectMode={selectMode}
                  isSelected={selection.has(c.contact)}
                  onToggleSelect={() => toggleSelect(c.contact)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT SLIDE PANEL ───────────────────────────────────── */}
      <div style={{
        width: selected && !selectMode ? 420 : 0, flexShrink: 0, overflow: "hidden",
        borderLeft: selected && !selectMode ? `1px solid ${T.border1}` : "none",
        background: T.bg1, display: "flex", flexDirection: "column",
        transition: "width 0.28s cubic-bezier(0.4,0,0.2,1)",
      }}>
        {selected && profile && !selectMode && (
          <>
            {/* Panel header */}
            <div style={{ padding: "14px 16px", borderBottom: `1px solid ${T.border1}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
              <Avatar name={profile.name || profile.phone || selected} size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {profile.name || profile.phone || cleanPhone(selected)}
                </div>
                <div style={{ fontSize: 11, color: T.text3, marginTop: 2 }}>{profile.phone || cleanPhone(selected)}</div>
              </div>
              <button onClick={() => { setSelected(null); setProfile(null); setConfirmDel(false); }}
                style={{ width: 28, height: 28, borderRadius: 8, border: `1px solid ${T.border1}`, background: T.bg2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.text3, flexShrink: 0 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* Tabs */}
            <div style={{ display: "flex", borderBottom: `1px solid ${T.border1}`, flexShrink: 0 }}>
              {[{ id: "profile", label: "Profile" }, { id: "chat", label: "Chat History" }].map(t => (
                <div key={t.id} onClick={() => setActiveTab(t.id)}
                  style={{ padding: "9px 18px", cursor: "pointer", fontSize: 12, fontWeight: 600, borderBottom: activeTab === t.id ? `2px solid ${T.indigo}` : "2px solid transparent", color: activeTab === t.id ? T.indigo : T.text3, marginBottom: -1, transition: "all 0.15s" }}>
                  {t.label}
                </div>
              ))}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center", paddingRight: 12 }}>
                <button onClick={() => setConfirmDel(true)}
                  style={{ width: 26, height: 26, borderRadius: 6, border: "1px solid rgba(244,63,94,0.3)", background: "rgba(244,63,94,0.08)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#f43f5e" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                </button>
                <button onClick={save} disabled={saving}
                  style={{ padding: "4px 12px", borderRadius: 7, border: "none", cursor: saving ? "not-allowed" : "pointer", fontFamily: base.font, fontSize: 11, fontWeight: 700, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", color: "#fff", opacity: saving ? 0.7 : 1 }}>
                  {saving ? "…" : "Save"}
                </button>
              </div>
            </div>

            {/* Tab content */}
            <div style={{ flex: 1, overflowY: "auto" }}>

              {/* PROFILE TAB */}
              {activeTab === "profile" && (
                <div style={{ padding: "16px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <Field label="Full Name">
                      <PanelInput value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))} placeholder="Ahmed Khan" />
                    </Field>
                    <Field label="Phone">
                      <PanelInput value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} placeholder="+92 300..." />
                    </Field>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                    <Field label="Industry">
                      <PanelInput value={profile.industry} onChange={e => setProfile(p => ({ ...p, industry: e.target.value }))} placeholder="e.g. Real Estate" />
                    </Field>
                    <Field label="Interested In">
                      <PanelInput value={profile.interestedIn} onChange={e => setProfile(p => ({ ...p, interestedIn: e.target.value }))} placeholder="e.g. Braces, NIW" />
                    </Field>
                  </div>
                  <Field label="Lead Status">
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {LEAD_STATUSES.map(s => (
                        <div key={s.value} onClick={() => setProfile(p => ({ ...p, leadStatus: s.value }))}
                          style={{ padding: "5px 11px", borderRadius: 20, cursor: "pointer", fontSize: 11, fontWeight: 600, transition: "all 0.15s", background: profile.leadStatus === s.value ? `${s.color}22` : T.bg0, color: profile.leadStatus === s.value ? s.color : T.text3, border: `1px solid ${profile.leadStatus === s.value ? s.color + "55" : T.border1}` }}>
                          {s.label || "None"}
                        </div>
                      ))}
                    </div>
                  </Field>
                  <Field label="Intention / Goal">
                    <PanelTextarea value={profile.intention} onChange={e => setProfile(p => ({ ...p, intention: e.target.value }))} placeholder="What does this person want?" rows={2} />
                  </Field>
                  <Field label="Tags">
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginBottom: 7 }}>
                      {(profile.tags || []).map(t => <Tag key={t} label={t} onRemove={() => removeTag(t)} />)}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 7 }}>
                      {PRESET_TAGS.filter(t => !(profile.tags || []).includes(t)).map(t => (
                        <div key={t} onClick={() => addTag(t)}
                          style={{ padding: "3px 8px", borderRadius: 20, cursor: "pointer", fontSize: 10, fontWeight: 600, color: T.text3, border: `1px solid ${T.border1}`, background: T.bg0 }}
                          onMouseOver={e => { e.currentTarget.style.borderColor = T.violet; e.currentTarget.style.color = T.violet; }}
                          onMouseOut={e  => { e.currentTarget.style.borderColor = T.border1; e.currentTarget.style.color = T.text3; }}>
                          + {t}
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 5 }}>
                      <input value={tagInput} onChange={e => setTagInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); } }}
                        placeholder="Custom tag… (Enter)"
                        style={{ flex: 1, padding: "5px 9px", background: T.bg0, border: `1px solid ${T.border1}`, borderRadius: 7, color: T.text1, fontSize: 11, outline: "none" }}
                        onFocus={e => (e.target.style.borderColor = T.violet)}
                        onBlur={e  => (e.target.style.borderColor = T.border1)} />
                      <Btn variant="ghost" size="sm" onClick={() => addTag(tagInput)}>Add</Btn>
                    </div>
                  </Field>
                </div>
              )}

              {/* CHAT HISTORY TAB */}
              {activeTab === "chat" && (
                <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  {chatLoading ? (
                    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div style={{ width: 24, height: 24, border: `2px solid ${T.border1}`, borderTopColor: T.indigo, borderRadius: "50%", animation: "spin 0.7s linear infinite" }} />
                    </div>
                  ) : chatHistory.length === 0 ? (
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 30 }}>
                      <div style={{ opacity: 0.2 }}><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: T.text2 }}>No messages yet</div>
                    </div>
                  ) : (
                    <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 6 }}>
                      <div style={{ fontSize: 11, color: T.text3, marginBottom: 8, textAlign: "center" }}>{chatHistory.length} messages</div>
                      {chatHistory.map((m, i) => (
                        <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-start" : "flex-end" }}>
                          <div style={{ maxWidth: "82%", padding: "8px 12px", borderRadius: m.role === "user" ? "12px 12px 12px 3px" : "12px 12px 3px 12px", background: m.role === "user" ? T.bg3 : "linear-gradient(135deg,rgba(99,102,241,0.18),rgba(99,102,241,0.08))", border: `1px solid ${m.role === "user" ? T.border1 : "rgba(99,102,241,0.22)"}`, color: T.text1, fontSize: 12, lineHeight: 1.6, wordBreak: "break-word" }}>
                            {m.content}
                          </div>
                          <div style={{ fontSize: 10, color: T.text3, marginTop: 2, padding: "0 2px" }}>
                            {m.role === "user" ? "You" : "Bot"} · {m.timestamp ? new Date(m.timestamp).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                          </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Confirm delete overlay ───────────────────────────────── */}
      {confirmDel && (
        <div onClick={() => setConfirmDel(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", backdropFilter: "blur(6px)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", animation: "fadeIn 0.15s ease" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: T.bg2, border: "1px solid rgba(244,63,94,0.3)", borderRadius: 18, padding: 28, width: 360, animation: "fadeUp 0.2s ease" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#f43f5e", marginBottom: 8 }}>Delete contact?</div>
            <div style={{ fontSize: 13, color: T.text2, marginBottom: 22, lineHeight: 1.6 }}>
              <strong style={{ color: T.text1 }}>{profile?.name || cleanPhone(selected)}</strong> and their entire chat history will be permanently deleted.
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={deleteProfile} disabled={deleting}
                style={{ flex: 1, padding: "10px", borderRadius: 10, background: "#f43f5e", color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: deleting ? "not-allowed" : "pointer", fontFamily: base.font }}>
                {deleting ? "Deleting…" : "Yes, Delete"}
              </button>
              <button onClick={() => setConfirmDel(false)}
                style={{ flex: 1, padding: "10px", borderRadius: 10, background: T.bg1, color: T.text2, border: `1px solid ${T.border1}`, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: base.font }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
